import GameLifeline from "../GameBoard/GameLifeline";
import { useState, useEffect } from "react";
import ReportQuestionModal from "../../components/Modal/ReportQuestionModal";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import CountdownTimer from "./Answer/CountdownTimer";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getGameDetailsAPI, useLifelineAPI } from "../../api/game";
import useToast from "../../hooks/useToast";
import Loader from "../../components/Loader/Loader";
import { useTranslation } from "react-i18next";
import PrimaryBtn from "../../components/button/PrimaryBtn";
import { getDefaultPortraitByGender, resolveCharacterUrlFromList } from "../../utils/charcter";
import { getCharacterListAPI } from "../../api/user";
import GameAnswerQuestionContent from "./Answer/GameAnswerQuestionContent";
import GameAnswerCorrectAnswerContent from "./Answer/GameAnswerCorrectAnswerContent";
import { useGameDetailsFromLayout } from "../../layouts/Game/GameDetailsContext";

function getLocalizedField(value: unknown, lang: string): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && !Array.isArray(value)) {
    const o = value as Record<string, string>;
    return String(o[lang] ?? o.en ?? o.ar ?? "");
  }
  return String(value);
}

const GameBackfireAnswer = () => {
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<string[]>([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTimerExpired, setIsTimerExpired] = useState(false);
  const SECOND_CHANCE_SECONDS = 20;
  const [isSecondChanceActive, setIsSecondChanceActive] = useState(false);
  const [isSecondChanceExpired, setIsSecondChanceExpired] = useState(false);
  const [playerTimerPaused, setPlayerTimerPaused] = useState(false);
  const [secondChanceKey, setSecondChanceKey] = useState(0);
  const [secondChanceSeconds, setSecondChanceSeconds] = useState(SECOND_CHANCE_SECONDS);
  const [elapsedSeconds, setElapsedSeconds] = useState(30);
  const [backfireTimerKey, setBackfireTimerKey] = useState(0);
  const [optionImagePreviewUrl, setOptionImagePreviewUrl] = useState<string | null>(null);

  const { t } = useTranslation();
  const gameDetailsFromLayout = useGameDetailsFromLayout();
  const setIsAnswerRevealed = gameDetailsFromLayout?.setIsAnswerRevealed;
  const setIsLifelineUsedOnQuestion = gameDetailsFromLayout?.setIsLifelineUsedOnQuestion;

  const { gameId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const currentLanguage = localStorage.getItem("i18nextLng") || "en";
  // Get the Backfire data from navigation state
  const questionDataFromState = location.state?.questionData;
  const challengerTeam = location.state?.challengerTeam;
  const opponentTeam = location.state?.opponentTeam;
  const lifelineId = location.state?.lifelineId;
  const teamLifelineId = location.state?.teamLifelineId;
  // Redirect if no data
  useEffect(() => {
    setIsLifelineUsedOnQuestion?.(true);
  }, [setIsLifelineUsedOnQuestion]);

  useEffect(() => {
    if (!questionDataFromState || !challengerTeam || !opponentTeam) {
      navigate(`/game/${gameId}`);
    }
  }, [questionDataFromState, challengerTeam, opponentTeam, gameId, navigate]);

  useEffect(() => {
    if (showCorrectAnswer) {
      setIsAnswerRevealed?.(true);
    }
  }, [showCorrectAnswer, setIsAnswerRevealed]);

  // Fetch game data from API for team info
  const { data: gameDetails, isLoading: isLoadingGame } = useQuery({
    queryKey: ["gameDetails", gameId],
    queryFn: () => getGameDetailsAPI(gameId as string),
    enabled: !!gameId,
  });

  const gameData = gameDetails?.data?.game;
  const teamData = gameData?.teams || [];
  const { data: portraitCharacterListData } = useQuery({
    queryKey: ["characterList", "portrait"],
    queryFn: () => getCharacterListAPI({ type: "portrait" }),
    staleTime: 1000 * 60 * 30,
  });
  const portraitCharacters = (portraitCharacterListData as any)?.data?.characters || [];

  // Get opponent team's participants
  const opponentTeamData = gameData?.teams?.find((t: any) => t._id === opponentTeam?.id);
  const opponentParticipants = opponentTeamData?.participants || [];

  // Auto-select if only one participant in the opponent team when modal opens
  useEffect(() => {
    if (showAnswerModal && opponentParticipants.length === 1) {
      setSelectedParticipantIds([opponentParticipants[0]?.user_id?._id]);
    }
  }, [showAnswerModal, opponentParticipants]);

  // Use lifeline mutation - final API call
  const { mutate: useLifeline } = useMutation({
    mutationFn: useLifelineAPI,
    onSuccess: async (data) => {
      if (isTimerExpired) {
        useToast(t("gameBackfireAnswer.questionMarkedAsTimeout"), "success");
      } else {
        useToast(data?.message || "Backfire completed successfully!", "success");
      }
      // Await invalidation so fresh data (updated points) is in cache before navigating
      await queryClient.invalidateQueries({ queryKey: ["gameDetails", gameId] });
      // Navigate back to game board
      navigate(`/game/${gameId}`);
    },
    onError: (error: any) => {
      useToast(error.message || t("gameBackfireAnswer.failedToCompleteBackfire"), "error");
      setIsSubmitting(false);
    },
  });

  // Handle participant selection - toggle multi-select
  const handleParticipantClick = (participantId: string) => {
    setSelectedParticipantIds((prev) =>
      prev.includes(participantId) ? prev.filter((id) => id !== participantId) : [...prev, participantId]
    );
  };

  // Handle correct answer - opponent team answered correctly
  const handleCorrectAnswer = () => {
    setShowAnswerModal(false);
    submitBackfireResult(selectedParticipantIds);
  };

  // Handle wrong answer - opponent team didn't answer correctly, points go to challenger
  const handleWrongAnswer = () => {
    if (!gameId || !questionDataFromState?.question_id || !challengerTeam?.id || !lifelineId) return;

    setIsSubmitting(true);

    // Call useLifeline API with the result
    useLifeline({
      game_id: gameId,
      question_id: questionDataFromState?.question_id,
      team_id: challengerTeam.id, // The team that used the lifeline
      opponent_team_id: opponentTeam.id,
      lifeline_id: lifelineId,
      team_lifeline_id: teamLifelineId,
      user_id: [], // Empty array if wrong, participant IDs if correct
      answered_team_id: null,
    } as any);
  };

  // Submit the Backfire result
  const submitBackfireResult = (userIds: string[]) => {
    if (!gameId || !questionDataFromState?.question_id || !challengerTeam?.id || !lifelineId) return;

    setIsSubmitting(true);

    // Call useLifeline API with the result
    useLifeline({
      game_id: gameId,
      question_id: questionDataFromState?.question_id,
      team_id: challengerTeam.id, // The team that used the lifeline
      opponent_team_id: opponentTeam.id,
      lifeline_id: lifelineId,
      team_lifeline_id: teamLifelineId,
      user_id: userIds, // Empty array if wrong, participant IDs if correct
      answered_team_id: opponentTeam.id,
    } as any);
  };

  // Handle timer expiration - just mark expired (no auto-submit)
  const handleTimerExpired = () => {
    if (isTimerExpired) return;
    setIsTimerExpired(true);
  };

  const handleStartSecondChance = () => {
    setIsSecondChanceExpired(false);
    setPlayerTimerPaused(false);
    setIsSecondChanceActive(true);
    setSecondChanceSeconds(SECOND_CHANCE_SECONDS);
    setSecondChanceKey((k) => k + 1);
  };

  const handleSecondChanceTimeUp = () => {
    setSecondChanceSeconds(0);
    setIsSecondChanceExpired(true);
  };

  const handleResumePlayerTimer = () => {
    setIsSecondChanceExpired(false);
    setIsSecondChanceActive(false);
    setPlayerTimerPaused(true);
  };

  // Show loading state
  if (isLoadingGame || isSubmitting || !questionDataFromState) {
    return (
      <div className="flex justify-center items-center h-full min-h-[50vh] bg-primary-ivory">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className="wrapper flex-1 flex flex-col sm:flex-row bg-no-repeat xl:bg-[length:100%_100%] bg-cover bg-bottom bg-primary-ivory px-6 gap-4">
        <section className="w-full md:w-[80%] flex justify-center items-center relative flex-1">
          <div className="w-full h-full flex justify-center items-center 2xl:pb-[1.9vw] md:pb-6 pb-5 landscape:max-lg:pb-5">
            <div className="w-full mx-auto relative z-20 h-full 2xl:pt-[1vw] xl:pt-3 pt-2 flex flex-col">

              <div className="hidden lg:flex absolute -top-1 start-10 z-10 flex justify-between w-[95%]">

                <div className="flex gap-2 items-center">
                  <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-orange flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                    <span className="skew-x-10 2xl:mt-[0.208vw] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-white font-garamond">
                      {t("gameBackfireAnswer.backfireChallenge")}
                    </span>
                  </div>
                  <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#E7E6E6] flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                    <span className="skew-x-10 2xl:mt-[0.208vw] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-black-300">
                      {challengerTeam?.name}
                    </span>
                  </div>
                  <span className="text-lg font-bold text-black-300">VS</span>
                  <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#E7E6E6] flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                    <span className="skew-x-10 2xl:mt-[0.208vw] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-black-300">
                      {opponentTeam?.name}
                    </span>
                  </div>
                </div>

                <div >
                  {isSecondChanceActive ? (
                    <CountdownTimer
                      isStopped={isSecondChanceExpired}
                      autoStart={!isSecondChanceExpired}
                      key={secondChanceKey}
                      initialTime={SECOND_CHANCE_SECONDS}
                      initialRemainingOverride={isSecondChanceExpired ? 0 : secondChanceSeconds}
                      onTimeUp={handleSecondChanceTimeUp}
                      onTimeUpdate={(seconds) => {
                        setSecondChanceSeconds(seconds);
                      }}
                      onResetClick={isSecondChanceExpired ? handleResumePlayerTimer : handleStartSecondChance}
                      onExpiredPlay={handleResumePlayerTimer}
                      handleStartSecondChance={handleStartSecondChance}
                    />
                  ) : (
                    <CountdownTimer
                      key={backfireTimerKey}
                      initialTime={30}
                      autoStart={questionDataFromState?.question_type !== "qr_code" && !playerTimerPaused}
                      isStopped={
                        showCorrectAnswer ||
                        showReportModal ||
                        showAnswerModal
                      }
                      onTimeUp={handleTimerExpired}
                      onTimeUpdate={(seconds) => {
                        setElapsedSeconds(seconds);
                      }}
                      onResetClick={() => {
                        setElapsedSeconds(30);
                        setIsTimerExpired(false);
                        setBackfireTimerKey((k) => k + 1);
                      }}
                      handleStartSecondChance={handleStartSecondChance}
                    />
                  )}
                </div>

                <button className="bg-primary-orange border border-black-300 rounded-md px-10 2xl:px-12 2xl:h-[3.229vw] xl:h-8 md:h-8 h-6 landscape:max-lg:h-7.5 w-fit items-center justify-center z-10 flex">
                  <div className="font-bold text-2xl 2xl:text-4xl absolute mt-1 text-white">
                    {questionDataFromState?.difficulty === "easy"
                      ? "200"
                      : questionDataFromState?.difficulty === "medium"
                        ? "400"
                        : "600"}
                  </div>
                </button>

              </div>




              {!showCorrectAnswer && (
                <button
                  onClick={() => setShowCorrectAnswer(true)}
                  className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-orange flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 landscape:max-lg:py-0.5 2xl:px-[1.042vw] lg:px-4 md:px-3  px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[1.5vw]/[1.5vw] xl:text-base/tight text-sm/tight font-bold text-white cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] absolute -bottom-0.5 end-5 z-10"
                >
                  <span className="skew-x-10 2xl:mt-[0.208vw]">{t("gameBackfireAnswer.viewCorrectAnswer")}</span>
                </button>
              )}

              <div className="flex lg:hidden justify-center gap-2  flex-wrap items-center">
                <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-orange flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                  <span className="skew-x-10 2xl:mt-[0.208vw] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-white font-garamond">
                    {t("gameBackfireAnswer.backfireChallenge")}
                  </span>
                </div>
                <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#E7E6E6] flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                  <span className="skew-x-10 2xl:mt-[0.208vw] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-black-300">
                    {challengerTeam?.name}
                  </span>
                </div>
                <span className="text-lg font-bold text-black-300">VS</span>
                <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#E7E6E6] flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                  <span className="skew-x-10 2xl:mt-[0.208vw] mt-1 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-black-300">
                    {opponentTeam?.name}
                  </span>
                </div>
              </div>


              <div className="flex lg:hidden my-3 justify-center gap-2  flex-wrap items-center">
                {isSecondChanceActive ? (
                  <CountdownTimer
                    isStopped={isSecondChanceExpired}
                    autoStart={!isSecondChanceExpired}
                    key={secondChanceKey}
                    initialTime={SECOND_CHANCE_SECONDS}
                    initialRemainingOverride={isSecondChanceExpired ? 0 : secondChanceSeconds}
                    onTimeUp={handleSecondChanceTimeUp}
                    onTimeUpdate={(seconds) => {
                      setSecondChanceSeconds(seconds);
                    }}
                    onResetClick={isSecondChanceExpired ? handleResumePlayerTimer : handleStartSecondChance}
                    onExpiredPlay={handleResumePlayerTimer}
                    handleStartSecondChance={handleStartSecondChance}
                  />
                ) : (
                  <CountdownTimer
                    key={backfireTimerKey}
                    initialTime={30}
                    autoStart={questionDataFromState?.question_type !== "qr_code" && !playerTimerPaused}
                    isStopped={
                      showCorrectAnswer ||
                      showReportModal ||
                      showAnswerModal
                    }
                    onTimeUp={handleTimerExpired}
                    onTimeUpdate={(seconds) => {
                      setElapsedSeconds(seconds);
                    }}
                    onResetClick={() => {
                      setElapsedSeconds(30);
                      setIsTimerExpired(false);
                      setBackfireTimerKey((k) => k + 1);
                    }}
                    handleStartSecondChance={handleStartSecondChance}
                  />
                )}

                <button className="bg-primary-orange border border-black-300 rounded-md px-10 2xl:px-12 2xl:h-[3.229vw] xl:h-8 md:h-8 h-6 landscape:max-lg:h-7.5 w-fit flex items-center justify-center">
                  <div className="font-bold text-2xl 2xl:text-4xl absolute mt-1 text-white">
                    {questionDataFromState?.difficulty === "easy"
                      ? "200"
                      : questionDataFromState?.difficulty === "medium"
                        ? "400"
                        : "600"}
                  </div>
                </button>
              </div>






              {!showCorrectAnswer && (
                <GameAnswerQuestionContent
                  questionData={questionDataFromState}
                  currentLanguage={currentLanguage}
                  isSecondChanceActive={isSecondChanceActive}
                  getPreviewTimerSeconds={() =>
                    isSecondChanceActive
                      ? isSecondChanceExpired
                        ? 0
                        : secondChanceSeconds
                      : elapsedSeconds
                  }
                  SECOND_CHANCE_SECONDS={SECOND_CHANCE_SECONDS}
                  setOptionImagePreviewUrl={setOptionImagePreviewUrl}
                  getLocalizedField={getLocalizedField}
                  t={t}
                />
              )}

              {/* Correct answer: shared layout + opponent team / none (backfire) */}
              {showCorrectAnswer && (
                <GameAnswerCorrectAnswerContent
                  setShowCorrectAnswer={setShowCorrectAnswer}
                  setIsSecondChanceActive={setIsSecondChanceActive}
                  questionData={questionDataFromState}
                  currentLanguage={currentLanguage}
                  setOptionImagePreviewUrl={setOptionImagePreviewUrl}
                  getLocalizedField={getLocalizedField}
                  t={t}
                  isSkipLifelineAnswerReveal={false}
                  gameData={
                    opponentTeam?.id
                      ? { teams: [{ team_name: opponentTeam.name, _id: opponentTeam.id }] }
                      : { teams: [] }
                  }
                  handleAnswerClick={() => setShowAnswerModal(true)}
                  handleNoneAnswered={handleWrongAnswer}
                  onNavigateNextQuestion={() => navigate(`/game/${gameId}`)}
                  noneAnsweredText={t("gameBackfireAnswer.none")}
                />
              )}
            </div>
          </div>
        </section>

        <section className="game-bottom-banner h-auto lg:h-full flex items-center relative justify-center">
          <div className="w-full 2xl:max-w-[65vw] max-w-335 mx-auto relative z-10 flex flex-col 2xl:gap-[1.25vw] xl:gap-1 md:gap-3 gap-2.5">
            {teamData?.map((team: any) => (
              <div key={team._id}>
                <GameLifeline
                  team={team}
                  score={team.total_points}
                  lifelines={team.lifelines}
                  isCurrentTurn={team._id === challengerTeam.id}
                  disableAllLifelines={true}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Participant Selection Modal */}
        {showAnswerModal && (
          <>
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-50">
              <div className="modal bg-white-500 2xl:rounded-[2.083vw] lg:rounded-[40px] md:rounded-4xl rounded-3xl 2xl:p-[2vw] xl:p-10 md:p-7 p-4 relative animate-[slideUp_0.3s_ease-out] lg:border-[0.2vw] border-3 border-primary overflow-auto lg:max-h-[calc(100dvh-11%)] max-h-[calc(100dvh-40px)] scroll-width-small">
                <div className="flex lg:gap-[1vw] gap-2 items-start justify-between w-full 2xl:mb-[1vw] md:mb-4 mb-3">
                  <h1 className="2xl:text-[1.563vw]/[1.7vw] xl:text-3xl/6 text-2xl/5 font-semibold text-primary-orange">
                    {t("gameBackfireAnswer.selectWhoAnsweredCorrectly")}
                  </h1>
                  {/* Close Button */}
                  <button
                    onClick={() => {
                      setShowAnswerModal(false);
                      setSelectedParticipantIds([]);
                    }}
                    className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300"
                  >
                    <svg
                      className="w-full h-full"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                        fill="#333938"
                      />
                    </svg>
                  </button>
                </div>

                <div className="w-full 2xl:mb-[1.042vw] mb-5">
                  <svg
                    className="w-full"
                    width="540"
                    height="1"
                    viewBox="0 0 540 1"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <line
                      y1="-0.5"
                      x2="540"
                      y2="-0.5"
                      transform="matrix(-1 0 0 1 540 1)"
                      stroke="url(#paint0_linear_backfire_participant)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_backfire_participant"
                        x1="0"
                        y1="0.5"
                        x2="540"
                        y2="0.5"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#0B3369" stopOpacity="0" />
                        <stop offset="0.5" stopColor="#7364FF" />
                        <stop offset="1" stopColor="#0B3369" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                {/* Participant List with Multi Selection */}
                <div className="grid grid-cols-2 2xl:gap-[0.833vw] md:gap-4 gap-3 border-2 border-black-300 bg-primary-ivory 2xl:p-[1vw] p-4 2xl:rounded-[0.833vw] rounded-xl">
                  {opponentParticipants.map((participant: any) => {
                    const participantId = participant?.user_id?._id;
                    const isSelected = selectedParticipantIds.includes(participantId);
                    const participantPortrait =
                      resolveCharacterUrlFromList(participant?.user_id?.profile_image, portraitCharacters) ||
                      getDefaultPortraitByGender(
                        participant?.user_id?.gender || participant?.gender,
                        portraitCharacters
                      ) ||
                      null;
                    return (
                      <button
                        key={participant._id}
                        onClick={() => handleParticipantClick(participantId)}
                        className={`cursor-pointer w-full flex items-center 2xl:gap-[0.6vw] gap-2`}
                      >
                        <div
                          className="2xl:w-[2.5vw] xl:w-[40px] lg:w-10 w-10 2xl:min-w-[2.5vw] xl:min-w-[40px] lg:min-w-10 min-w-10 2xl:h-[2.5vw] xl:h-[40px] lg:h-10 h-10 aspect-square overflow-hidden
                              bg-primary-light-orange skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] flex justify-center items-center"
                        >
                          {participantPortrait ? (
                            <img
                              src={participantPortrait}
                              alt={participant?.user_id?.full_name}
                              className="w-full h-full object-cover"
                            />
                          ) : <span className="text-center text-xl">
                            {participant?.user_id?.full_name?.charAt(0)?.toUpperCase() || "?"}
                          </span>}


                        </div>
                        <div
                          className={`flex-1 skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 flex items-center 2xl:py-[0.54vw] py-1.5 2xl:px-[1.042vw] lg:px-4 md:px-3  px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[0.9375vw]/[1.04vw] text-base/tight font-bold cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]
                        ${isSelected ? "bg-primary-orange text-white" : "bg-[#F2BF7D] text-black-300"}`}
                        >
                          <span className={`skew-x-[10deg] 2xl:mt-[0.208vw] mt-1 text-start w-full`}>
                            {participant?.user_id?.full_name}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Selected count */}
                {/* {selectedParticipantIds.length > 0 && (
                  <p className="2xl:text-[0.75vw] text-xs text-primary-orange font-bold 2xl:mt-[0.8vw] mt-3 text-center">
                    {selectedParticipantIds.length}{" "}
                    {selectedParticipantIds.length > 1
                      ? t("gameAnswer.participantsSelected")
                      : t("gameAnswer.participantSelected")}
                  </p>
                )} */}

                {/* Confirm Button */}
                <PrimaryBtn
                  onClick={handleCorrectAnswer}
                  className="!mb-0 w-full min-w-full 2xl:mt-[1vw] mt-4"
                >
                  {selectedParticipantIds.length > 0
                    ? `${t("gameAnswer.confirmAnswer")} (${selectedParticipantIds.length})`
                    : t("gameAnswer.confirmAnswer")}
                </PrimaryBtn>
              </div>
            </div>
            <style>{`
                @keyframes slideUp {
                  from {
                    opacity: 0;
                    transform: translateY(20px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }
              `}</style>
          </>
        )}

        {optionImagePreviewUrl && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4"
            onClick={() => setOptionImagePreviewUrl(null)}
            role="presentation"
          >
            <div
              className="relative max-w-[min(90vw,720px)] max-h-[min(85dvh,720px)] p-2 bg-white-500 rounded-xl border-[1.5px] border-black-300 shadow-lg"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <button
                type="button"
                onClick={() => setOptionImagePreviewUrl(null)}
                className="absolute -top-2 -end-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-primary-yellow border-[1.5px] border-black-300 shadow-[0px_2px_0px_#110706] hover:scale-105 transition-transform"
                aria-label="Close"
              >
                <span className="text-black-300 font-bold leading-none text-lg">×</span>
              </button>
              <img
                src={optionImagePreviewUrl}
                alt=""
                className="max-w-full max-h-[min(80dvh,680px)] w-auto h-auto object-contain mx-auto block rounded-md"
              />
            </div>
          </div>
        )}

        {showReportModal && (
          <ReportQuestionModal
            isOpen={showReportModal}
            onClose={() => setShowReportModal(false)}
            questionData={questionDataFromState}
            quistionIdToReport={questionDataFromState?.question_id}
          />
        )}
      </div>
    </>
  );
};

export default GameBackfireAnswer;
